import React, { useState, useEffect, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { toast } from 'react-toastify';
import { useCms } from '../context/CmsContext';

const GridOverlay = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    style={{ opacity: 0.08 }}
    aria-hidden="true"
  >
    <defs>
      <pattern id="hero-grid" width="48" height="48" patternUnits="userSpaceOnUse">
        <path d="M48 0H0V48" fill="none" stroke="#ffffff" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#hero-grid)" />
  </svg>
);

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001';

const DEFAULT_IMAGES = [
  'https://i.postimg.cc/439zXXBC/Flowise_01_15_2026_06_05_PM.png',
  'https://i.postimg.cc/vmVWGGXJ/Flowise_01_15_2026_06_05_PM_(1).png',
];

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
};

const STATS = [
  { value: '+30%', label: 'Redução de custos' },
  { value: '+50%', label: 'Eficiência operacional' },
  { value: '90%', label: 'Satisfação do cidadão' },
];

const COLOR_MAP = {
  orange: { bg: '#F47920', hover: '#d96a10', text: '#fff', border: '#F47920' },
  blue:   { bg: '#1B4F8A', hover: '#163f6e', text: '#fff', border: '#1B4F8A' },
  white:  { bg: '#ffffff', hover: '#f1f5f9', text: '#1B4F8A', border: '#ffffff' },
  dark:   { bg: '#0f172a', hover: '#1e293b', text: '#fff', border: '#0f172a' },
};

const HeroButton = ({ text, btnStyle, color, onClick, href, showArrow }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.orange;

  const baseClass = 'inline-flex items-center justify-center gap-2 font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5 cursor-pointer';

  const getStyle = () => {
    if (btnStyle === 'solid') return { background: c.bg, color: c.text, borderRadius: '8px', padding: '14px 32px', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' };
    if (btnStyle === 'outline') return { background: 'transparent', color: c.border, border: `1.5px solid ${c.border}`, borderRadius: '8px', padding: '12.5px 32px' };
    return { background: 'transparent', color: `${c.border}99`, textDecoration: 'underline', textUnderlineOffset: '4px', padding: '8px 0' };
  };

  const handleMouseEnter = (e) => {
    if (btnStyle === 'solid') e.currentTarget.style.background = c.hover;
    if (btnStyle === 'outline') e.currentTarget.style.background = `${c.border}15`;
    if (btnStyle === 'link') e.currentTarget.style.color = c.border;
  };
  const handleMouseLeave = (e) => {
    if (btnStyle === 'solid') e.currentTarget.style.background = c.bg;
    if (btnStyle === 'outline') e.currentTarget.style.background = 'transparent';
    if (btnStyle === 'link') e.currentTarget.style.color = `${c.border}99`;
  };

  const props = { className: baseClass, style: getStyle(), onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave };

  const content = (
    <>
      {text}
      {showArrow && (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
        </svg>
      )}
    </>
  );

  if (href) return <a href={href} {...props}>{content}</a>;
  return <button onClick={onClick} {...props}>{content}</button>;
};

const HeroInner = () => {
  const cms = useCms('hero', {});
  const images = (cms.image_urls || []).map(resolveUrl).filter(Boolean).length > 0
    ? (cms.image_urls || []).map(resolveUrl).filter(Boolean)
    : DEFAULT_IMAGES;

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [images.length]);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '' });
  const [leadFormTime, setLeadFormTime] = useState(null);
  const [leadCaptchaToken, setLeadCaptchaToken] = useState(null);
  const [sending, setSending] = useState(false);
  const leadCaptchaRef = useRef(null);
  const textColRef = useRef(null);
  const [textColHeight, setTextColHeight] = useState(null);

  useEffect(() => {
    const update = () => {
      if (textColRef.current) setTextColHeight(textColRef.current.offsetHeight);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  const handleLeadChange = (e) => {
    const { name, value } = e.target;
    setLeadForm(prev => ({ ...prev, [name]: value }));
  };

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!leadCaptchaToken) {
      toast.error('Complete a verificação de segurança.');
      return;
    }
    setSending(true);
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001';
    fetch(`${API_BASE}/api/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...leadForm, source: 'hero_cta', website: '', form_time: leadFormTime || (Date.now() - 5000), captcha_token: leadCaptchaToken }),
    })
      .then(async r => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || (body.errors && body.errors[0]?.msg) || 'Erro ao enviar.');
        toast.success('Solicitação enviada! Em breve entraremos em contato.');
        setLeadForm({ name: '', email: '', phone: '' });
        setLeadCaptchaToken(null);
        leadCaptchaRef.current?.resetCaptcha();
        setIsDemoModalOpen(false);
      })
      .catch(err => {
        toast.error(err.message || 'Erro ao enviar. Tente novamente.');
        leadCaptchaRef.current?.resetCaptcha();
        setLeadCaptchaToken(null);
      })
      .finally(() => setSending(false));
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1B4F8A 55%, #163f6e 100%)' }}
    >
      <GridOverlay />

      <div className="relative container mx-auto px-6 md:px-12 pt-28 pb-20 md:pt-36 md:pb-28">

        <div className="flex flex-col lg:flex-row items-start gap-16">
          {/* Left: copy */}
          <div ref={textColRef} className="lg:w-1/2 text-center lg:text-left">
            <div className="inline-flex items-center gap-2.5 mb-7 justify-center lg:justify-start">
              <span className="w-1 h-4" style={{ background: '#F47920' }} />
              <span className="text-xs font-bold uppercase tracking-widest text-blue-200/90">
                Gestão Pública Inteligente
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight">
              Planejamento,<br />
              controle e gestão.<br />
              <span style={{ color: '#F47920' }}>
                Tudo em um só lugar.
              </span>
            </h1>

            <p className="text-lg text-blue-100/80 mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Leve inovação à gestão pública. Automatize rotinas, reduza burocracias e aumente a eficiência com decisões inteligentes.
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-4 mb-16">
              <HeroButton
                text={cms.cta_primary || 'Falar com um Especialista'}
                btnStyle={cms.cta_primary_style || 'solid'}
                color={cms.cta_primary_color || 'orange'}
                onClick={() => setIsDemoModalOpen(true)}
                showArrow
              />
              <HeroButton
                text={cms.cta_secondary || 'Ver Planos'}
                btnStyle={cms.cta_secondary_style || 'outline'}
                color={cms.cta_secondary_color || 'white'}
                onClick={() => document.querySelector('#planos')?.scrollIntoView({ behavior: 'smooth' })}
              />
              {cms.cta_tertiary && (
                <HeroButton
                  text={cms.cta_tertiary}
                  btnStyle={cms.cta_tertiary_style || 'link'}
                  color={cms.cta_tertiary_color || 'white'}
                  href={cms.cta_tertiary_href || '/plataforma'}
                />
              )}
            </div>

            {/* Mini stats */}
            <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-8">
              {STATS.map((s, i) => (
                <div key={i} className="text-center lg:text-left">
                  <p className="text-2xl font-extrabold" style={{ color: '#F47920' }}>{s.value}</p>
                  <p className="text-xs text-blue-200/70 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: image carousel — framed as a browser window for credibility */}
          <div className="lg:w-1/2 relative flex flex-col justify-center items-center">
            <div
              className="relative w-full max-w-2xl rounded-lg overflow-hidden border border-white/10 shadow-2xl"
              style={{ background: '#0b1320' }}
            >
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10" style={{ background: '#0f1b2e' }}>
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                <span className="ml-3 text-[11px] text-white/40 font-medium">painel.nexus.gov.br</span>
              </div>
              <div
                className="relative overflow-hidden w-full"
                style={textColHeight ? { maxHeight: textColHeight } : {}}
              >
                {images.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt={`Dashboard Nexus ${idx + 1}`}
                    style={textColHeight ? { maxHeight: textColHeight } : {}}
                    className={`w-full h-auto object-contain transition-opacity duration-1000 ${
                      idx === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'
                    }`}
                    onError={e => {
                      e.target.onerror = null;
                      e.target.src = 'https://placehold.co/600x400/1e3a5f/60a5fa?text=Nexus+Dashboard';
                    }}
                  />
                ))}
              </div>
            </div>
            {/* Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`rounded-full transition-all duration-200 ${
                    idx === currentImageIndex ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/30'
                  }`}
                  aria-label={`Slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-12">
          <path d="M0 60L1440 60L1440 20C1200 60 960 0 720 20C480 40 240 0 0 20L0 60Z" fill="white" />
        </svg>
      </div>

      {/* Demo modal */}
      {isDemoModalOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={e => { if (e.target === e.currentTarget) setIsDemoModalOpen(false); }}
          onMouseEnter={() => { if (!leadFormTime) setLeadFormTime(Date.now()); }}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-8 pt-8 pb-6 border-b border-gray-100 flex justify-between items-start">
              <div>
                <h3 className="text-xl font-extrabold text-gray-900">Solicitar Demonstração</h3>
                <p className="text-sm text-gray-500 mt-1">Nossa equipe entrará em contato em até 24h.</p>
              </div>
              <button
                onClick={() => setIsDemoModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors mt-1"
                aria-label="Fechar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleLeadSubmit} className="px-8 py-6 space-y-4" onFocus={() => { if (!leadFormTime) setLeadFormTime(Date.now()); }}>
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                  <input type="text" name="website" tabIndex="-1" autoComplete="off" />
                </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Nome Completo</label>
                <input
                  type="text"
                  name="name"
                  value={leadForm.name}
                  onChange={handleLeadChange}
                  required
                  placeholder="Seu nome"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Email Institucional</label>
                <input
                  type="email"
                  name="email"
                  value={leadForm.email}
                  onChange={handleLeadChange}
                  required
                  placeholder="seu.email@prefeitura.gov.br"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Telefone / WhatsApp</label>
                <input
                  type="tel"
                  name="phone"
                  value={leadForm.phone}
                  onChange={handleLeadChange}
                  placeholder="(86) 99999-9999"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow bg-gray-50"
                />
              </div>
              <HCaptcha
                sitekey={HCAPTCHA_SITE_KEY}
                onVerify={token => setLeadCaptchaToken(token)}
                onExpire={() => setLeadCaptchaToken(null)}
                ref={leadCaptchaRef}
                theme="light"
              />
              <button
                type="submit"
                disabled={sending || !leadCaptchaToken}
                className="w-full bg-blue-900 text-white font-bold px-8 py-4 rounded-lg shadow-md hover:bg-blue-800 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed text-sm mt-2"
              >
                {sending ? 'Enviando...' : 'Solicitar Demonstração Gratuita'}
              </button>
              <p className="text-xs text-gray-400 text-center">
                Ao enviar, você concorda com nossa{' '}
                <a href="/privacidade" className="underline hover:text-gray-600">Política de Privacidade</a>.
              </p>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

const HeroSection = () => <HeroInner />;

export default HeroSection;
